// What's New: `WHATS-NEW.md` rendered in a modal — once on the first visit of the day, and any time from the account menu.
//
// ⚠ THE MODAL'S STATE LIVES HERE (2026-09-16), for the same reason as `feedback-button.tsx`: the account menu in the
// workspace header — three literals in a file another session is editing — and the one on the player home both want to
// open it, and the workspace should not grow state for a note. The menu row dispatches `compendium:whats-new` on `window`;
// the ONE mounted instance (in `app.tsx`) listens. `openWhatsNew()` is the only way in.
//
// ⚠ ONCE A DAY, PER DEVICE. `localStorage["vttmaps.whatsnew.day"]` holds the day it last opened by itself; a different day
// opens it again. Every localStorage read and write is guarded — a private window throws, and a note that fails to
// remember simply opens again tomorrow. The key keeps the `vttmaps.` prefix like every other (see HANDOFF).
import { useCallback, useEffect, useState } from "react";
import { localDay, longDate, parseWhatsNew } from "../lib/whats-new";
import text from "../../WHATS-NEW.md?raw";

export const WHATS_NEW_EVENT = "compendium:whats-new";
const DAY_KEY = "vttmaps.whatsnew.day";

/** Open the note from anywhere — the account menu's row. */
export function openWhatsNew(): void {
  window.dispatchEvent(new CustomEvent(WHATS_NEW_EVENT));
}

const SECTIONS = parseWhatsNew(text);
/** Newest day first for the modal — the file has grown out of order, and 180+ entries are a lot to scroll past. */
const DAYS = [...SECTIONS].sort((a, b) => b.date.localeCompare(a.date));

/** Render `**bold**` and `*italic*` from a What's New line — the file is written in that light markdown and it was
 *  showing the asterisks raw. No links or code in the file, so this stays a two-token parser. */
function inline(s: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0, k = 0, m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(m[1] !== undefined ? <strong key={k++}>{m[1]}</strong> : <em key={k++}>{m[2]}</em>);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

export function WhatsNew({ autoOpen = false }: {
  /** Open by itself on the first visit of the day. True on the GM workspace and the player home; nowhere else. */
  autoOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // ⚠ OPENS COLLAPSED (2026-09-28: "you shouldn't have to scroll through 2-3 pages of updates to accept"). Only the newest
  // day shows; the rest wait behind one tap. Reset every time it opens, so it never opens already-expanded.
  const [showEarlier, setShowEarlier] = useState(false);
  const show = useCallback(() => { setShowEarlier(false); setOpen(true); }, []);
  useEffect(() => {
    window.addEventListener(WHATS_NEW_EVENT, show);
    return () => window.removeEventListener(WHATS_NEW_EVENT, show);
  }, [show]);
  useEffect(() => {
    if (!autoOpen || SECTIONS.length === 0) return;
    const today = localDay();
    let last: string | null = null;
    try { last = localStorage.getItem(DAY_KEY); } catch { /* private window: it opens, and will again tomorrow */ }
    if (last === today) return;
    try { localStorage.setItem(DAY_KEY, today); } catch { /* same */ }
    setShowEarlier(false);
    setOpen(true);
  }, [autoOpen]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;
  const [latest, ...earlier] = DAYS;
  const day = (s: { date: string; items: string[] }) => (
    <section key={s.date} className="whatsnew-day">
      <h3>{longDate(s.date)}</h3>
      <ul>{s.items.map((line, i) => <li key={i}>{inline(line)}</li>)}</ul>
    </section>
  );
  return (
    <div className="modal-back topmost" onClick={() => setOpen(false)}>
      <div className="modal whatsnew" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="What's new">
        {/* ⚠ "Got it" LIVES IN A STICKY HEADER (2026-09-28): it stays at the top so the note can be dismissed without
            scrolling past every past update. */}
        <div className="whatsnew-head">
          <h2 className="panel-title">What&rsquo;s new</h2>
          <button className="primary" onClick={() => setOpen(false)}>Got it</button>
        </div>
        {DAYS.length === 0 && <p className="muted">Nothing written up yet.</p>}
        {latest && day(latest)}
        {earlier.length > 0 && !showEarlier && (
          <button className="whatsnew-more" onClick={() => setShowEarlier(true)}>
            Show earlier updates ({earlier.length} more {earlier.length === 1 ? "day" : "days"})
          </button>
        )}
        {showEarlier && earlier.map(day)}
      </div>
    </div>
  );
}
